import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTheme } from '../contexts/ThemeContext';
import SaveSnippetModal from './SaveSnippetModal';
import logger from '../utils/simpleLogger';
import '../styles/pdf-viewer.css';
import { ThumbnailsSidebar, OutlineSidebar, BookmarksSidebar } from './pdf/PDFSidebars';
import { authManager } from '../utils/authManager';
import type { PDFViewerModalProps, OutlineItem, ScrollMode } from './pdf/types/pdf-viewer.types';
import {
  usePDFNavigation,
  usePDFZoom,
  usePDFSearch,
  usePDFCapture,
  usePDFBookmarks,
  usePDFRotation,
  usePDFPresentation,
  usePDFScrollMode,
  usePDFSnippetOverlay,
} from './pdf/hooks';
import {
  PDFToolbar,
  PDFSearchBar,
  PDFCaptureOverlay,
  PDFSnippetOverlay,
  PDFPresentationControls,
} from './pdf/components';

// Configure PDF.js worker — use static file from public/ directory
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  isOpen,
  onClose,
  documentPath,
  documentName,
  documentId,
  projectId,
  page,
  highlightText,
  snippetCoordinates,
  embedded = false,
  onError,
  onPageChange,
  onClosePanel,
  onSnippetSaved,
}) => {
  const { theme } = useTheme();

  // Debug: Log props (cuando cambia documentPath o page)
  useEffect(() => {
    if (documentPath) {
      logger.info('📄 PDFViewerModal - Props:', {
        isOpen,
        documentPath,
        documentName,
        documentId,
        projectId,
        page,
        highlightText,
        embedded,
      });
    }
  }, [documentPath, page]);

  // PDF file configuration - keep token in URL for react-pdf compatibility
  // react-pdf doesn't reliably send custom headers, so we use query parameter
  const pdfFile = React.useMemo(() => {
    const url = documentPath;
    const token = authManager.getToken();

    // Ensure token is in URL for react-pdf
    try {
      const urlObj = new URL(url);
      // If token is not in URL, add it
      if (token && !urlObj.searchParams.has('token')) {
        urlObj.searchParams.set('token', token);
      }
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, try to append token if we have one
      if (token && !url.includes('token=')) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}token=${token}`;
      }
      return url;
    }
  }, [documentPath]);

  // PDF options - react-pdf may not send headers reliably, so we rely on URL token
  const pdfOptions = React.useMemo(() => {
    // Still try to send headers as fallback, but URL token is primary
    const token = authManager.getToken();
    if (!token) return undefined;

    return {
      httpHeaders: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [documentPath]);

  // Refs
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // State for PDF document
  const [numPages, setNumPages] = useState<number>(0);
  const [outline, setOutline] = useState<OutlineItem[] | null>(null);
  const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number } | null>(
    null
  );
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [showOutline, setShowOutline] = useState(false);

  // Navigation hook
  const { pageNumber, setPageNumber, goToPreviousPage, goToNextPage } = usePDFNavigation({
    initialPage: page,
    numPages,
    onPageChange,
  });

  // Zoom hook
  const {
    scale,
    fitMode,
    showFitDropdown,
    fitDropdownPosition,
    fitButtonRef,
    setScale,
    setFitMode,
    setShowFitDropdown,
    setFitDropdownPosition,
    zoomIn,
    zoomOut,
  } = usePDFZoom({
    containerRef,
    pageDimensions,
    isOpen,
  });

  // Search hook
  const {
    searchText,
    searchInput,
    showSearchBar,
    textMatches,
    currentMatchIndex,
    setSearchInput,
    setShowSearchBar,
    handleSearch,
    handleSearchSubmit,
    clearSearch,
    goToPreviousMatch,
    goToNextMatch,
    textRenderer,
  } = usePDFSearch({
    highlightText,
    isOpen,
    pageNumber,
    setPageNumber,
    onPageChange,
    searchInputRef,
  });

  // Capture hook
  const {
    captureMode,
    isDrawing,
    captureRect,
    startPoint,
    capturedImage,
    showSaveModal,
    setCaptureMode,
    setIsDrawing,
    setCaptureRect,
    setStartPoint,
    setCapturedImage,
    setShowSaveModal,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleAutoSaveSnippet,
    handleSaveSnippet,
  } = usePDFCapture({
    containerRef,
    pageRefs,
    documentId,
    documentName,
    documentPath,
    pageNumber,
    scale,
    projectId,
    onSnippetSaved,
  });

  // Bookmarks hook
  const {
    bookmarks,
    showBookmarksPanel,
    setShowBookmarksPanel,
    addBookmark,
    removeBookmark,
    goToBookmark,
  } = usePDFBookmarks({
    documentId,
    pageNumber,
    onPageChange,
  });

  // Rotation hook
  const { rotation, rotateClockwise, rotateCounterClockwise, resetRotation } = usePDFRotation();

  // Presentation hook
  const { presentationMode, enterPresentationMode, exitPresentationMode } = usePDFPresentation({
    containerRef,
    goToPreviousPage,
    goToNextPage,
  });

  // Scroll mode hook
  const { scrollMode, setScrollMode } = usePDFScrollMode({
    containerRef,
    isOpen,
    goToPreviousPage,
    goToNextPage,
  });

  // Snippet overlay hook
  const { snippetOverlayRect } = usePDFSnippetOverlay({
    snippetCoordinates,
    pageNumber,
    scale,
    numPages,
    containerRef,
    pageRefs,
  });

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // ESC = Cerrar modal, buscador, modo captura o presentación
      if (e.key === 'Escape') {
        if (presentationMode) {
          exitPresentationMode();
        } else if (captureMode) {
          setCaptureMode(false);
          setCaptureRect(null);
          setStartPoint(null);
          setIsDrawing(false);
        } else if (showSearchBar) {
          clearSearch();
        } else {
          onClose();
        }
      }

      // Ctrl+F / Cmd+F = Abrir buscador
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearchBar(true);
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }

      // Presentation mode navigation
      if (presentationMode) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          goToPreviousPage();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault();
          goToNextPage();
        }
      }

      // Rotate with R key (Ctrl+R / Cmd+R)
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        rotateClockwise();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyboard);
      if (!embedded) {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyboard);
      if (!embedded) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [
    isOpen,
    onClose,
    embedded,
    showSearchBar,
    captureMode,
    presentationMode,
    goToPreviousPage,
    goToNextPage,
    clearSearch,
    setCaptureMode,
    setCaptureRect,
    setStartPoint,
    setIsDrawing,
    exitPresentationMode,
    rotateClockwise,
  ]);

  const onDocumentLoadSuccess = async ({ numPages, _pdf }: { numPages: number; _pdf?: any }) => {
    logger.info('✅ PDF cargado exitosamente:', numPages, 'páginas');
    setNumPages(numPages);

    // Load outline if available
    try {
      let pdf = _pdf;
      if (!pdf) {
        const loadingTask = pdfjs.getDocument({ url: documentPath });
        pdf = await loadingTask.promise;
      }
      const outlineData = await pdf.getOutline();
      if (outlineData && outlineData.length > 0) {
        setOutline(outlineData as OutlineItem[]);
      }
    } catch (error) {
      logger.info('ℹ️ No hay outline disponible en este PDF');
    }
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('❌ Error cargando PDF:', error);
    console.error('   documentPath:', documentPath);
    console.error('   documentName:', documentName);

    // Call error callback if provided
    if (onError) {
      const errorMessage = error.message || 'Error desconocido al cargar el PDF';
      onError(errorMessage);
    }
  };

  // Helper functions
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentPath;
    link.download = documentName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    const pdfUrl = page ? `${documentPath}#page=${page}` : documentPath;
    window.open(pdfUrl, '_blank');
  };

  const navigateToOutlineItem = useCallback(
    async (item: OutlineItem) => {
      try {
        if (item.dest) {
          const dest = Array.isArray(item.dest) ? item.dest : [item.dest];
          if (dest.length > 0 && typeof dest[0] === 'object' && dest[0].num !== undefined) {
            const targetPage = dest[0].num;
            setPageNumber(targetPage);
            onPageChange?.(targetPage);
          }
        } else if (item.url) {
          window.open(item.url, '_blank');
        }
      } catch (error) {
        console.error('Error navigating to outline item:', error);
      }
    },
    [setPageNumber, onPageChange]
  );

  if (!isOpen) return null;

  // Common content for both modes
  const pdfContent = (
    <>
      {/* Header Unificado - Una sola fila con todo */}
      {!presentationMode && (
        <PDFToolbar
          theme={theme}
          documentName={documentName}
          searchText={searchText}
          textMatches={textMatches}
          scale={scale}
          fitMode={fitMode}
          showFitDropdown={showFitDropdown}
          fitDropdownPosition={fitDropdownPosition}
          fitButtonRef={fitButtonRef}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onFitModeChange={setFitMode}
          onShowFitDropdown={setShowFitDropdown}
          onFitDropdownPositionChange={setFitDropdownPosition}
          pageNumber={pageNumber}
          numPages={numPages}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
          captureMode={captureMode}
          onCaptureModeToggle={() => setCaptureMode(!captureMode)}
          goToPreviousMatch={goToPreviousMatch}
          goToNextMatch={goToNextMatch}
          showThumbnails={showThumbnails}
          onShowThumbnails={setShowThumbnails}
          outline={outline}
          showOutline={showOutline}
          onShowOutline={setShowOutline}
          onAddBookmark={addBookmark}
          bookmarks={bookmarks}
          showBookmarksPanel={showBookmarksPanel}
          onShowBookmarksPanel={setShowBookmarksPanel}
          rotation={rotation}
          onRotateCounterClockwise={rotateCounterClockwise}
          onRotateClockwise={rotateClockwise}
          onResetRotation={resetRotation}
          presentationMode={presentationMode}
          onPresentationModeToggle={presentationMode ? exitPresentationMode : enterPresentationMode}
          scrollMode={scrollMode}
          onScrollModeToggle={() => {
            const newMode: ScrollMode = scrollMode === 'continuous' ? 'page-by-page' : 'continuous';
            setScrollMode(newMode);
          }}
          onDownload={handleDownload}
          onOpenInNewTab={handleOpenInNewTab}
          embedded={embedded}
          onClosePanel={onClosePanel}
          onClose={onClose}
        />
      )}

      {/* PDF Viewer Container with Sidebars */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebars */}
        {!presentationMode && showThumbnails && numPages > 0 && (
          <ThumbnailsSidebar
            theme={theme}
            pdfUrl={pdfFile}
            numPages={numPages}
            currentPage={pageNumber}
            onPageSelect={(p) => {
              setPageNumber(p);
              onPageChange?.(p);
            }}
            onClose={() => setShowThumbnails(false)}
          />
        )}
        {!presentationMode && showOutline && outline && outline.length > 0 && (
          <OutlineSidebar
            theme={theme}
            outline={outline}
            onNavigate={navigateToOutlineItem}
            onClose={() => setShowOutline(false)}
          />
        )}
        {!presentationMode && showBookmarksPanel && bookmarks.length > 0 && (
          <BookmarksSidebar
            theme={theme}
            bookmarks={bookmarks}
            currentPage={pageNumber}
            onGoToBookmark={goToBookmark}
            onRemoveBookmark={removeBookmark}
            onClose={() => setShowBookmarksPanel(false)}
          />
        )}

        {/* PDF Viewer */}
        <div
          ref={containerRef}
          className={`flex-1 overflow-auto scrollbar-thin ${
            theme.colors.bg.secondary
          } flex items-start justify-center p-4 relative ${
            presentationMode ? 'presentation-mode' : ''
          }`}
        >
          <Document
            key={documentPath} // Solo remount cuando cambie el documento
            file={pdfFile}
            options={pdfOptions}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nb-accent"></div>
              </div>
            }
            error={
              <div className={`${theme.colors.action.danger} p-8 text-center`}>
                <p>Error al cargar el PDF.</p>
                <button
                  onClick={handleOpenInNewTab}
                  className="mt-4 text-sm text-nb-accent hover:underline"
                >
                  Intentar abrir en nueva pestaña
                </button>
              </div>
            }
          >
            <div
              style={{
                transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
                transformOrigin: 'center center',
              }}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                customTextRenderer={textRenderer as any}
                className="shadow-lg"
                inputRef={(ref) => {
                  pageRefs.current[pageNumber] = ref || null;
                }}
                onLoadSuccess={(page) => {
                  // Get page dimensions for fit mode calculations
                  const viewport = page.getViewport({ scale: 1 });
                  setPageDimensions({ width: viewport.width, height: viewport.height });
                }}
              />
            </div>
          </Document>

          {/* Snippet Overlay */}
          <PDFSnippetOverlay theme={theme} snippetOverlayRect={snippetOverlayRect} />

          {/* Capture Mode Overlay */}
          {captureMode && (
            <PDFCaptureOverlay
              theme={theme}
              captureRect={captureRect}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => {
                if (isDrawing) {
                  setIsDrawing(false);
                  setCaptureRect(null);
                  setStartPoint(null);
                }
              }}
            />
          )}

          {/* Floating Search Bar */}
          {showSearchBar && !captureMode && (
            <PDFSearchBar
              theme={theme}
              searchInput={searchInput}
              searchText={searchText}
              searchInputRef={searchInputRef}
              onSearchChange={handleSearch}
              onSearchSubmit={handleSearchSubmit}
              onClear={clearSearch}
            />
          )}

          {/* Presentation Mode Controls */}
          {presentationMode && (
            <PDFPresentationControls
              theme={theme}
              pageNumber={pageNumber}
              numPages={numPages}
              onPreviousPage={goToPreviousPage}
              onNextPage={goToNextPage}
              onExit={exitPresentationMode}
            />
          )}
        </div>
      </div>

      {/* Info Footer */}
      {searchText && !embedded && (
        <div
          className={`px-6 py-3 border-t ${theme.colors.border.primary} ${theme.colors.bg.secondary}`}
        >
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 w-2 h-2 bg-nb-warning rounded-full mt-1.5" />
            <div className="flex-1 min-w-0">
              <p className={`text-xs ${theme.colors.text.secondary} mb-1`}>Texto resaltado:</p>
              <p className={`text-sm ${theme.colors.text.primary} italic line-clamp-2`}>
                "{searchText}"
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // For embedded mode, render without modal backdrop
  if (embedded) {
    return (
      <>
        <div className="w-full h-full flex flex-col">{pdfContent}</div>

        {/* Save Snippet Modal */}
        <SaveSnippetModal
          isOpen={showSaveModal}
          onClose={() => {
            setShowSaveModal(false);
            setCapturedImage(null);
          }}
          onSave={handleSaveSnippet}
          previewImage={capturedImage || undefined}
          documentName={documentName}
          pageNumber={pageNumber}
        />
      </>
    );
  }

  // For modal mode, render with backdrop
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

        {/* Modal Content */}
        <div
          className={`relative w-full h-full max-w-7xl max-h-[95vh] m-4 ${theme.colors.bg.tertiary} border ${theme.colors.border.primary} rounded-lg shadow-2xl flex flex-col overflow-hidden`}
        >
          {pdfContent}
        </div>
      </div>

      {/* Save Snippet Modal */}
      <SaveSnippetModal
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setCapturedImage(null);
          setCaptureRect(null); // Limpiar también al cancelar
        }}
        onSave={handleSaveSnippet}
        previewImage={capturedImage || undefined}
        documentName={documentName}
        pageNumber={pageNumber}
      />
    </>
  );
};

export default PDFViewerModal;
